import {Component, forwardRef, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild} from '@angular/core';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';
import {Observable, of, Subject} from 'rxjs';
import {catchError, debounceTime, distinctUntilChanged, switchMap, takeUntil} from 'rxjs/operators';
import {ProductService} from '../services/product.service';
import {MultiSelectInputComponent} from '../../ui-components/ui-forms/multi-select-input.component';

@Component({
    selector: 'app-product-select',
    template: `
    <app-multi-select-input
      [multiple]="true"
      [config]="dropUp ? {
        noResultsFound: 'No products found',
        placeholder: 'Select products',
        search: true,
        displayKey: 'title',
        dropUp: true,
        backendSearch: true
      } : {
        noResultsFound: 'No products found',
        placeholder: 'Select products',
        search: true,
        displayKey: 'title',
        backendSearch: true
      }"
      [options]="options"
      [ngModel]="value"
      (change)="onInternalChange($event)"
      (searchChange)="onSearch($event)"
    ></app-multi-select-input>

    <div class="raw-id-input" *ngIf="allowRawIds">
      <label>Add products by ID (optional)</label>
      <div class="raw-id-input__row">
        <textarea
          rows="2"
          class="form-control"
          placeholder="Paste product IDs, e.g. 123, 456 789"
          [(ngModel)]="rawIdsText"
        ></textarea>
        <button type="button" class="btn btn-secondary" (click)="addRawIds()" [disabled]="resolvingRawIds">
          {{ resolvingRawIds ? 'Adding…' : 'Add' }}
        </button>
      </div>
      <small *ngIf="rawIdsStatus" class="raw-id-input__status">{{ rawIdsStatus }}</small>
    </div>
  `,
    styles: [
        `
      .raw-id-input {
        margin-top: 8px;
      }
      .raw-id-input__row {
        display: flex;
        gap: 8px;
        align-items: flex-start;
      }
      .raw-id-input__row textarea {
        flex: 1;
      }
      .raw-id-input__status {
        display: block;
        margin-top: 4px;
        color: #6c757d;
      }
    `,
    ],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => ProductSelectComponent),
            multi: true,
        },
    ],
    standalone: false
})
export class ProductSelectComponent implements OnInit, OnChanges, OnDestroy, ControlValueAccessor {
  @Input() allowWingame = false;
  @Input() allowRegular = false;
  @Input() allowAll = false;
  @Input() dropUp = false;
  /** When true, show an optional textarea to add products by raw id (useful for long lists). */
  @Input() allowRawIds = false;

  @ViewChild(MultiSelectInputComponent) private multiSelect?: MultiSelectInputComponent;

  options: any[] = [];
  value: any[] = [];

  rawIdsText = '';
  rawIdsStatus = '';
  resolvingRawIds = false;

  private onChange: (val: any) => void = () => {};
  private onTouched: () => void = () => {};
  private destroy$ = new Subject<void>();
  // debounced product search: one request after typing settles, switchMap cancels stale ones
  private searchQuery$ = new Subject<string>();

  constructor(private productService: ProductService) {}

  ngOnInit() {
    this.searchQuery$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap(term => {
          const req = this.buildProductRequest(term);
          return req ? req.pipe(catchError(() => of({results: []}))) : of({results: []});
        }),
        takeUntil(this.destroy$),
      )
      .subscribe(res => (this.options = res.results.map(p => ({id: p.id, title: p.title}))));
    this.load();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.allowWingame || changes.allowRegular || changes.allowAll) {
      this.load();
    }
  }

  onSearch(term: string) {
    this.searchQuery$.next(term);
  }

  onInternalChange(event: any) {
    this.value = event.value;
    this.onChange(this.value);
    this.onTouched();
  }

  writeValue(val: any) {
    this.value = val || [];
  }

  registerOnChange(fn: any) {
    this.onChange = fn;
  }

  registerOnTouched(fn: any) {
    this.onTouched = fn;
  }

  // Resolve pasted ids to full products, merge them into the current selection and push the result
  // into the multi-select so the user sees the resolved titles (and can tell which ids were wrong).
  addRawIds() {
    const ids = this.parseIds(this.rawIdsText);
    if (!ids.length) {
      this.rawIdsStatus = 'Enter one or more numeric product IDs.';
      return;
    }

    const current = Array.isArray(this.value) ? this.value : [];
    const currentIds = new Set(current.map(item => this.idOf(item)));
    const newIds = ids.filter(id => !currentIds.has(id));
    if (!newIds.length) {
      this.rawIdsStatus = 'All entered products are already selected.';
      return;
    }

    this.resolvingRawIds = true;
    this.rawIdsStatus = '';
    this.productService.get(newIds).subscribe({
      next: (products: any) => {
        const resolved = (products || []).map((p: any) => ({id: p.id, title: p.title}));
        const foundIds = new Set(resolved.map((p: any) => p.id));
        const notFound = newIds.filter(id => !foundIds.has(id));

        const merged = [...current, ...resolved];
        // Keep options aware of the resolved products so the dropdown/deselect behave consistently.
        const optionIds = new Set(this.options.map(o => this.idOf(o)));
        this.options = [...this.options, ...resolved.filter((p: any) => !optionIds.has(p.id))];

        this.value = merged;
        this.onChange(merged);
        this.onTouched();
        this.multiSelect?.setSelection(merged, this.options);

        this.rawIdsText = '';
        this.rawIdsStatus = this.buildStatus(resolved.length, notFound);
        this.resolvingRawIds = false;
      },
      error: () => {
        this.rawIdsStatus = 'Failed to look up products. Please try again.';
        this.resolvingRawIds = false;
      },
    });
  }

  private parseIds(text: string): number[] {
    const found = (text?.match(/\d+/g) || []).map(n => parseInt(n, 10)).filter(n => n > 0);
    return Array.from(new Set(found));
  }

  private idOf(item: any): number {
    return item && typeof item === 'object' ? item.id : item;
  }

  private buildStatus(addedCount: number, notFound: number[]): string {
    const parts: string[] = [];
    if (addedCount) {
      parts.push(`Added ${addedCount} product${addedCount === 1 ? '' : 's'}.`);
    }
    if (notFound.length) {
      parts.push(`Not found: ${notFound.join(', ')}.`);
    }
    return parts.join(' ') || 'No matching products found.';
  }

  private buildProductRequest(search?: string): Observable<any> | null {
    if (!this.allowAll && !this.allowWingame && !this.allowRegular) {
      return null;
    }
    const filters: any = {type: 'available'};
    if (search) filters.name = search;
    if (!this.allowAll) {
      if (this.allowWingame && !this.allowRegular) filters.product_type = 'wingame';
      if (this.allowRegular && !this.allowWingame) filters.product_type = 'shop_products';
    }
    return this.productService.getProducts(filters);
  }

  private load(search?: string) {
    const req = this.buildProductRequest(search);
    if (!req) {
      this.options = [];
      return;
    }
    req.subscribe(res => {
      this.options = res.results.map(p => ({id: p.id, title: p.title}));
    });
  }
}
